import type { OrganisationTaxRate } from '@my/directory-api';

/**
 * Options for a VAT-rate override picker (Charges & Costs, Cargo Definitions,
 * a cargo's products): a first "empty" option (the VAT treatment's rate on a
 * charge or cargo, inherit on a product), then the ACTIVE sales schemas as
 * "Name (23%)" — every type, so a product can be zero-rated or exempt, not only
 * a reduced Standard rate. The value stored is the schema id.
 *
 * An override only ever applies when the VAT treatment lands on the standard
 * line — an export stays zero-rated whatever is picked here.
 *
 * A stored id that is not in the list (a schema since made inactive or
 * deleted) stays as an extra, flagged option, so the select does not fall back
 * to the first option and a save does not quietly change it.
 */
export function taxRateOptions(
  rates: OrganisationTaxRate[],
  current: string,
  emptyLabel: string,
  notInListLabel: string,
): Array<{ value: string; label: string }> {
  const offered = rates
    .filter((r) => r.status === 'Active' && r.invoiceType !== 'Purchases')
    .sort((a, b) => a.displayOrder - b.displayOrder);
  const options = [
    { value: '', label: emptyLabel },
    ...offered.map((r) => ({ value: r.id, label: taxRateLabel(r) })),
  ];
  if (current && !offered.some((r) => r.id === current)) {
    const stored = rates.find((r) => r.id === current);
    options.push({ value: current, label: stored ? `${taxRateLabel(stored)} — ${notInListLabel}` : notInListLabel });
  }
  return options;
}

/** "Reduced rate (13.5%)" — a schema as the pickers and badges show it. */
export function taxRateLabel(rate: Pick<OrganisationTaxRate, 'name' | 'taxPercentage'>): string {
  return `${rate.name} (${formatPct(rate.taxPercentage)}%)`;
}

/** "13.5%" — the short form for grid badges. */
export function taxRateShort(rate: Pick<OrganisationTaxRate, 'taxPercentage'>): string {
  return `${formatPct(rate.taxPercentage)}%`;
}

function formatPct(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)));
}
